import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, MapPin, Trophy } from 'lucide-react';
import proTournamentInfoService, {
  withoutHubs,
  tournamentDay,
  ProTournamentInfo,
} from '../../services/proTournamentInfoService';
import { buildFileUrl, sortByTimeline } from './ProTournamentsSection';
import DynamicSlider from '../slider/DynamicSlider';
import { t as tr } from '../../i18n';

/** Slider-Location (Web + App): Desktop Inline-Karte nach der 2. Karte, Mobile unter der Sektion */
export const PDC_CALENDAR_SLIDER_LOCATION = 'home_pdc_calendar';
export const PDC_CALENDAR_SLIDER_INDEX = 2;

const ACCENT = '#3fced1';
const MAX_ITEMS = 10;

/** Kompakte Datums-Range, z. B. „12.–20. Juli" oder „28. Juli – 3. Aug.". */
export const formatDateRange = (t: ProTournamentInfo): string => {
  // UTC-Kalendertage lesen, sonst kippt das 23:59:59Z-Ende auf den Folgetag (2026-09-17)
  const start = typeof t.startDate === 'number' ? tournamentDay(t.startDate) : null;
  const end = typeof t.endDate === 'number' ? tournamentDay(t.endDate) : null;
  if (!start) return t.dateLabel || tr('web.component.home-new-tournaments.termin-folgt');
  const month = (d: Date) => d.toLocaleDateString('de-DE', { month: 'short' });
  if (!end || start.toDateString() === end.toDateString()) {
    return `${start.getDate()}. ${month(start)}`;
  }
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()}.–${end.getDate()}. ${month(start)}`;
  }
  return `${start.getDate()}. ${month(start)} – ${end.getDate()}. ${month(end)}`;
};

/**
 * Nächster konkreter Termin eines bereits GESTARTETEN Dachturniers (PDC Pro Tour,
 * Premier League, WM über den Jahreswechsel): die erste Schedule-Session ab
 * heute (Tagesanfang). Liefert null für kommende Turniere (Startdatum reicht),
 * für gestartete Turniere ohne kommende Session und ohne Schedule.
 * Pure Funktion – Grundlage für Badge UND Zusatzzeile (2026-09-15, Marcel).
 */
export const nextScheduleEntry = (
  t: ProTournamentInfo,
  now: number = Date.now(),
): { ms: number; label: string | null } | null => {
  const startMs = typeof t.startDate === 'number' ? t.startDate : null;
  if (startMs === null || startMs > now) return null;
  const day = new Date(now);
  const todayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const upcoming = (t.schedule ?? [])
    .filter((e) => typeof e.date === 'number' && (e.date as number) >= todayStart)
    .sort((a, b) => (a.date as number) - (b.date as number));
  if (upcoming.length === 0) return null;
  const next = upcoming[0];
  const label = (next.sessionLabel || next.round || '').trim() || null;
  return { ms: next.date as number, label };
};

const formatDay = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getDate()}. ${d.toLocaleDateString('de-DE', { month: 'short' })}`;
};

/**
 * Badge-Text: „Ab 12. Juli" für kommende Turniere. Bei bereits GESTARTETEN
 * Dachturnieren (PDC Pro Tour: startDate längst vorbei) wäre „Ab 9. Feb"
 * irreführend — dann „Nächster Termin: 22. Sep." aus dem Schedule.
 */
export const formatStartsAt = (t: ProTournamentInfo, now: number = Date.now()): string | null => {
  const startMs = typeof t.startDate === 'number' ? t.startDate : null;
  if (startMs === null) return null;
  if (startMs > now) return `Ab ${formatDay(startMs)}`;
  const next = nextScheduleEntry(t, now);
  if (!next) return null; // gestartet, keine kommende Session bekannt
  return tr('web.component.home-pdc-calendar.naechster-termin', { ms: formatDay(next.ms) });
};

/** Zusatzzeile unter dem Titel: Name der nächsten Session (z. B. „Players Championship 21"). */
export const nextSessionLabel = (t: ProTournamentInfo, now: number = Date.now()): string | null =>
  nextScheduleEntry(t, now)?.label ?? null;

/**
 * Kurzbeschreibung für die Karte (2026-09-15, Marcel: „zwei Zeilen, sonst wirkt
 * der Bereich leer"): intro › seoDescription › overview, Whitespace normalisiert.
 * Die Zeilenbegrenzung macht das CSS (line-clamp-2).
 */
export const cardDescription = (t: ProTournamentInfo): string | null => {
  const raw = t.intro || t.seoDescription || t.overview || '';
  const text = String(raw).replace(/\s+/g, ' ').trim();
  return text.length > 0 ? text : null;
};

/** Status-Chip im Content-Bereich über dem Titel (eckig, nie auf dem Bild). */
const CalendarStatusBadge: React.FC<{ tournament: ProTournamentInfo }> = ({ tournament }) => {
  const status = tournament.status;
  if (status === 'LIVE') {
    return (
      <span className="mb-2 inline-flex items-center gap-1 self-start rounded bg-red-500/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
        {tr('web.component.matches-section.live')}
      </span>
    );
  }
  if (status === 'COMPLETED') {
    return (
      <span className="mb-2 inline-flex items-center self-start rounded bg-zinc-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-300">
        {tr('web.component.event-timeline-card.beendet')}
      </span>
    );
  }
  const startsAt = formatStartsAt(tournament);
  if (!startsAt) return null;
  return (
    <span
      className="mb-2 inline-flex items-center self-start rounded px-2 py-0.5 text-[10px] font-bold text-black"
      style={{ backgroundColor: ACCENT }}
    >
      {startsAt}
    </span>
  );
};

const CalendarCard: React.FC<{ tournament: ProTournamentInfo; onClick: () => void }> = ({
  tournament,
  onClick,
}) => {
  const nextSession = nextSessionLabel(tournament);
  const description = cardDescription(tournament);
  const [imgError, setImgError] = useState(false);
  const image = buildFileUrl(tournament.thumbnail || tournament.heroImage || tournament.logo);
  const showImage = image && !imgError;
  const location = [tournament.venue, tournament.city].filter(Boolean).join(', ');

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-[220px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
    >
      {/* Bild / Fallback */}
      <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-800">
        {showImage ? (
          // Original statt 400×400-Thumb: die quadratischen Thumbnails
          // croppen die Breitformat-Banner ("rangezoomt", Marcel 2026-09-01)
          <img
            src={image as string}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="relative flex h-full w-full items-center justify-center bg-zinc-900">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  tr('web.component.home-pdc-calendar.radial-gradient-60-80-at'),
              }}
            />
            <Trophy size={26} className="text-zinc-600" />
          </div>
        )}
      </div>

      {/* Status-Chip + Name + Datum + Ort */}
      <div className="flex flex-1 flex-col p-2.5">
        <CalendarStatusBadge tournament={tournament} />
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {tournament.shortName || tournament.name || tr('web.component.home-live-pdc-lead.pdc-turnier')}
        </h3>
        {/* Dachturnier (2026-09-15): welcher Termin ist der nächste? */}
        {nextSession && (
          <p className="mt-1 line-clamp-1 text-[11px] font-medium text-gray-300">{nextSession}</p>
        )}
        {/* Kurzbeschreibung, max. 2 Zeilen (2026-09-15) */}
        {description && (
          <p className="mt-1.5 line-clamp-2 text-[11px] leading-snug text-gray-400">{description}</p>
        )}
        <div className="mt-auto space-y-1 pt-1.5 text-[11px] text-gray-500">
          <div className="flex items-center gap-1.5">
            <CalendarDays size={11} className="shrink-0" style={{ color: ACCENT }} />
            <span className="truncate">{formatDateRange(tournament)}</span>
          </div>
          {location && (
            <div className="flex items-center gap-1.5">
              <MapPin size={11} className="shrink-0" />
              <span className="truncate">{location}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

/**
 * "PDC-Kalender" (Hauptspalte der Startseite): kompakte Turnier-Karten als
 * horizontale Snap-Scroll-Reihe. Sortierung: laufende zuerst, dann kommende
 * (nächstes zuerst), dann die letzten beendeten. Klick → /turniere/:slug,
 * Header-Link → /pdc-turniere. Blendet sich bei leeren Daten aus.
 */
const HomePdcCalendar: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tournaments, setTournaments] = useState<ProTournamentInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    proTournamentInfoService
      .list({ limit: 50 })
      .then((list) => {
        if (!cancelled) setTournaments(withoutHubs(list.filter((t) => t && t.slug)));
      })
      .catch(() => {
        /* Section bleibt einfach ausgeblendet */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // LIVE → kommende (nächstes zuerst) → letzte beendete (neueste zuerst)
  const sorted = useMemo(() => sortByTimeline(tournaments).slice(0, MAX_ITEMS), [tournaments]);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (!loading && sorted.length === 0) return null;

  return (
    <section aria-label={tr('web.component.home-live-pdc-lead.pdc-kalender')}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="section-heading text-base font-bold text-white">{tr('web.component.home-live-pdc-lead.pdc-kalender')}</h2>
        <div className="flex items-center gap-2">
          <Link
            to="/pdc-turniere"
            className="flex items-center gap-1 text-xs font-semibold transition-colors hover:text-white"
            style={{ color: ACCENT }}
          >
            {tr('web.component.home-pdc-calendar.alle-turniere')}{' '}<ChevronRight size={14} />
          </Link>
          {!loading && sorted.length > 3 && (
            <div className="hidden items-center gap-1.5 sm:flex">
              <button
                type="button"
                aria-label={tr('web.component.chats-manager.zurueck')}
                onClick={() => scrollBy(-360)}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                aria-label={tr('web.component.home-darts-knowledge.weiter')}
                onClick={() => scrollBy(360)}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex snap-x gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[196px] w-[220px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
              />
            ))
          : sorted.map((tournament, index) => (
              <React.Fragment key={tournament.slug || tournament.id}>
                {/* Desktop-Inline-Slider (Marcel 2026-09-15): streckt sich auf Kartenhöhe,
                    Breite aus dem Slide-Format; ohne Slides rendert er nichts */}
                {index === PDC_CALENDAR_SLIDER_INDEX && (
                  <DynamicSlider location={PDC_CALENDAR_SLIDER_LOCATION} className="hidden w-auto shrink-0 self-stretch snap-start lg:block" />
                )}
                <CalendarCard tournament={tournament} onClick={() => navigate(`/turniere/${tournament.slug}`)} />
              </React.Fragment>
            ))}
      </div>
      {/* Mobile: Slider unter der Sektion */}
      {!loading && <DynamicSlider location={PDC_CALENDAR_SLIDER_LOCATION} className="mt-3 w-full lg:hidden" />}
    </section>
  );
};

export default HomePdcCalendar;
